import { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LucideIcon, Crown, Building2, UserCog, Heart } from 'lucide-react';
import { cn } from '@/lib/utils';

export type UserRole = 'super_admin' | 'agency_admin' | 'supervisor' | 'patient';

interface QuickAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  variant?: 'default' | 'outline' | 'secondary';
}

interface WelcomeBannerProps {
  role: UserRole;
  userName: string;
  userEmail?: string;
  agencyName?: string;
  quickActions?: QuickAction[];
  children?: ReactNode;
}

const roleConfig: Record<UserRole, { 
  title: string; 
  icon: LucideIcon; 
  badge: string; 
  gradient: string;
  badgeClass: string;
}> = {
  super_admin: {
    title: 'Super Admin',
    icon: Crown,
    badge: 'Unlimited Access',
    gradient: 'from-amber-500/10 to-yellow-500/10 border-amber-500/20',
    badgeClass: 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-0'
  },
  agency_admin: {
    title: 'Agency Admin',
    icon: Building2,
    badge: 'Agency Scope',
    gradient: 'from-blue-500/10 to-indigo-500/10 border-blue-500/20',
    badgeClass: 'bg-blue-500/10 text-blue-600 border-blue-500/20'
  },
  supervisor: {
    title: 'Supervisor',
    icon: UserCog,
    badge: 'Team Access',
    gradient: 'from-purple-500/10 to-violet-500/10 border-purple-500/20',
    badgeClass: 'bg-purple-500/10 text-purple-600 border-purple-500/20'
  },
  patient: {
    title: 'Patient',
    icon: Heart,
    badge: 'Personal Health',
    gradient: 'from-green-500/10 to-emerald-500/10 border-green-500/20',
    badgeClass: 'bg-green-500/10 text-green-600 border-green-500/20'
  }
};

export default function WelcomeBanner({ 
  role, 
  userName, 
  userEmail,
  agencyName, 
  quickActions = [],
  children 
}: WelcomeBannerProps) {
  const config = roleConfig[role];
  const IconComponent = config.icon;

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <Card className={cn(
      'bg-gradient-to-r border overflow-hidden',
      config.gradient
    )}>
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Welcome Message */}
          <div className="flex items-start gap-4">
            <div className={cn(
              'p-3 rounded-xl shrink-0',
              role === 'super_admin' ? 'bg-gradient-to-br from-amber-500 to-yellow-500' : 'bg-primary/10'
            )}>
              <IconComponent className={cn(
                'h-6 w-6',
                role === 'super_admin' ? 'text-white' : 'text-primary'
              )} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-semibold text-foreground">
                  {getGreeting()}, {userName}!
                </h2>
                <Badge className={config.badgeClass}>
                  {config.badge}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {role === 'patient' 
                  ? "Track your health assessments and stay informed about your wellness."
                  : `Welcome to your ${config.title} dashboard.`
                }
              </p>
              {agencyName && role !== 'patient' && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Building2 className="h-3.5 w-3.5" />
                  {agencyName}
                </div>
              )}
              {userEmail && (
                <p className="text-xs text-muted-foreground/70">{userEmail}</p>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          {quickActions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {quickActions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || (index === 0 ? 'default' : 'outline')}
                  size="sm"
                  onClick={action.onClick}
                  className="shrink-0"
                >
                  <action.icon className="h-4 w-4 mr-2" />
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Optional children content */}
        {children && (
          <div className="mt-4 pt-4 border-t border-border/50">
            {children}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
